import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { styles } from '@sdir/sds';
import { BackLink } from '@sdir/blueprint.aps/lib/components';
import { NavigationButtons } from '../../Molecules';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

export interface ApplicationLayoutProps {
  topHeading: React.ReactElement | string;
  heading: React.ReactElement | string;
  subHeading?: React.ReactElement | string;
  sideContent?: any;
  children: any;
  exitLabel?: string;
  mainContentMaxWidth?: string;
  hideTopHeading?: boolean;
  hideNavigation?: boolean;
  narrowSide?: boolean;
  largeGap?: boolean;
  navBackLabel?: string;
  navNextLabel?: string;
  canNavBack?: boolean;
  canNavNext?: boolean;
  hideHeading?: boolean;
  timeline?: React.ReactElement | string | null;
  onExitClick?: () => void;
  onNavNextClick?: () => void;
  onNavBackClick?: () => void;
}

export const ApplicationLayout: React.FC<ApplicationLayoutProps> = ({
  topHeading,
  heading,
  subHeading,
  children,
  sideContent,
  exitLabel,
  navBackLabel,
  navNextLabel,
  canNavNext,
  canNavBack,
  hideTopHeading = false,
  hideNavigation = false,
  narrowSide = false,
  largeGap = false,
  hideHeading = false,
  mainContentMaxWidth,
  timeline,
  onExitClick,
  onNavBackClick,
  onNavNextClick,
}) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();

  const handleNavBackClick = useCallback(() => {
    if (onNavBackClick) {
      onNavBackClick();
    } else {
      navigate(-1);
    }
  }, [navigate, onNavBackClick]);

  const handleExitClick = useCallback(() => {
    if (onExitClick) {
      onExitClick();
    } else {
      navigate(routes.certificateOverview);
    }
  }, [navigate, onExitClick]);

  return (
    <Container>
      {!hideHeading && (
        <>
          <BackTop hasTimeline={!!timeline}>
            {exitLabel && <BackLink text={exitLabel} iconColor="#fff" onClick={handleExitClick} />}
          </BackTop>
          <div className="heading__top">
            <Headings>
              {!hideTopHeading && <H1>{topHeading}</H1>}
              <H3>{heading}</H3>
              {subHeading && <H4>{subHeading}</H4>}
            </Headings>
            {timeline && <TimeLine>{timeline}</TimeLine>}
          </div>
        </>
      )}

      <ContentWrapper
        hasSideContent={!!sideContent}
        mainContentMaxWidth={mainContentMaxWidth}
        narrowSide={narrowSide}
        largeGap={largeGap}
      >
        <div className="main-area">{children}</div>
        {sideContent && <div className="side-area">{sideContent}</div>}
        <div className="nav-area">
          {!hideNavigation && (
            <NavigationButtons
              backButtonText={navBackLabel || intl.formatMessage({ id: 'general.form.back' })}
              nextButtonText={navNextLabel || intl.formatMessage({ id: 'general.form.next' })}
              backButtonOnClick={handleNavBackClick}
              nextButtonOnClick={onNavNextClick}
              backDisabled={!canNavBack}
              nextDisabled={!canNavNext}
            />
          )}
        </div>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  margin: 3rem 0;
  width: 100%;

  .heading__top {
    width: 100%;
    display: grid;
    grid-template-columns: auto auto;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    .heading__top {
      grid-template-columns: auto;
    }
  }
`;

const BackTop = styled.div<{ hasTimeline: boolean }>`
  padding-bottom: 6rem;
  & > button {
    float: right;

    & .icon-wrapper svg {
      fill: #ffffff;
    }
  }

  ${({ hasTimeline }) =>
    !hasTimeline &&
    css`
      padding-bottom: 0;
      & > button {
        margin: 20px 0 -40px 0;
      }
    `}
`;

const ContentWrapper = styled.div<{
  hasSideContent: boolean;
  mainContentMaxWidth?: string;
  narrowSide: boolean;
  largeGap: boolean;
}>`
  display: grid;
  grid-template-rows: auto auto;
  grid-row-gap: 3rem;
  margin-top: 3rem;
  ${({ hasSideContent, narrowSide, largeGap }) =>
    hasSideContent &&
    css`
      grid-template-columns: ${narrowSide ? '5fr 3fr' : '2fr 3fr'};
      grid-column-gap: ${largeGap ? '20rem' : '4rem'};
      @media screen and (max-width: ${styles.screen.large}) {
        grid-column-gap: 4rem;
      }
    `}

  .main-area {
    grid-row: 1;
    grid-column: 1;
    ${({ mainContentMaxWidth }) => mainContentMaxWidth && `max-width: ${mainContentMaxWidth};`}
  }

  .nav-area {
    grid-row: 2;
    grid-column: 1;

    > * {
      margin-bottom: 0;
    }
  }

  .side-area {
    grid-row: 1 / span 2;
    grid-column: 2;
    position: relative;
    min-height: 500px;
  }

  @media screen and (max-width: ${styles.screen.medium}) {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .side-area {
      min-height: 450px;

      .side-content {
        position: relative;
      }

      .filegroup-selected-view {
        min-height: 400px;
      }
    }
  }
`;

const H1 = styled.h1`
  font-size: 2rem;
  color: ${styles.colors.primary3};
  font-family: ${styles.fonts.stfMedium};
  font-weight: normal;
`;

const H3 = styled.h1`
  font-weight: 600;
  font-size: 3rem;
`;

const H4 = styled.h4`
  font-size: 2rem;
  font-family: ${styles.fonts.stfBook};
  color: ${styles.colors.font.text};
`;

const TimeLine = styled.div`
  width: 100%;
`;
const Headings = styled.div`
  width: 100%;
`;
